import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  DataObjectVersionOptionBadge,
  DataObjectVersionOptionLabel,
  DataObjectVersionSelect,
} from '../../../dataobject/DataObjectVersionSelect';
import { formatDateTimeShort } from '../../utils';
import translations from './FormDataObjectVersionSelect.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { DataObjectVersionType } from '../../../dataobject/types';

const { addTranslations, t } = i18nNsFactory('form-data-object-version-select');
addTranslations(translations);

export type FormDataObjectVersionSelectProps = {
  name: string;
  versions: DataObjectVersionType[] | undefined;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  wrapperHeight?: string;
  allowClear?: boolean;
  sortVersions?: (
    versions: DataObjectVersionType[] | undefined,
  ) => DataObjectVersionType[];
  getStatusLabel?: (statusCode?: string | null) => React.ReactNode;
  renderCalculationTimeInLabel?: boolean;
  size?: 'small' | 'middle' | 'large';
};

const defaultSortVersions = (versions: DataObjectVersionType[] | undefined) =>
  [...(versions || [])].sort(
    (a, b) =>
      new Date(b.calculationTime ?? 0).getTime() -
      new Date(a.calculationTime ?? 0).getTime(),
  );

export const FormDataObjectVersionSelect: FC<
  FormDataObjectVersionSelectProps
> = ({
  name,
  versions,
  isLoading,
  disabled,
  placeholder,
  wrapperHeight = 'auto',
  allowClear,
  sortVersions = defaultSortVersions,
  renderCalculationTimeInLabel = true,
  size,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <DataObjectVersionSelect<DataObjectVersionType>
          activeVersionId={value ?? null}
          versions={versions || []}
          onChange={(v) => onChange(v ?? null)}
          disabled={isLoading || disabled}
          placeholder={placeholder}
          wrapperHeight={wrapperHeight}
          allowClear={allowClear}
          sortVersions={sortVersions}
          size={size}
          renderOption={(v) => ({
            label: (
              <DataObjectVersionOptionLabel
                gap="small"
                className={v.statusCode || 'DRAFT'}
              >
                {(v.versionNumber || v.minorNumber) && (
                  <>
                    <DataObjectVersionOptionBadge
                      count={`${v.versionNumber}${
                        v?.minorNumber ? `.${v.minorNumber}` : ''
                      }`}
                      showZero
                      color="black"
                      size="small"
                    />{' '}
                  </>
                )}
                {renderCalculationTimeInLabel &&
                  formatDateTimeShort(v.calculationTime)}{' '}
                {t(`status.${v?.statusCode ?? 'DRAFT'}`)}
              </DataObjectVersionOptionLabel>
            ),
            value: v.id,
            className: v.statusCode || 'DRAFT',
          })}
        />
      )}
    />
  );
};
